/**
 * Completivos del inicial pendientes, a pantalla completa (se abre desde
 * Iniciales). Ventas financiadas con inicial pactado mayor que lo abonado;
 * con plan de fechas (ver lib/completivoplan) cada venta muestra lo vencido,
 * lo por vencer y el próximo pago, y "Ver plan" abre el detalle de los pagos.
 *
 * La sucursal se consulta en el servidor (que aplica el alcance del usuario);
 * estado, ventana de días y búsqueda filtran en el navegador.
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, StatCard, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import { formatDocumentoAuto } from "lib/documento";
import { imprimirListadoGrande } from "lib/impresionhtml";
import { estadoPlanCompletivo, fechaCorta } from "lib/completivoplan";
import { PlanCompletivoResumen } from "components/completivos/PlanCompletivo";

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const codigoSucursal = (label) => {
  const s = String(label || "")
    .split("-")[0]
    .trim();
  return s === "0" ? "" : s;
};

const VENTANAS = [7, 15, 30, 60, 90];

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

// Fecha ISO límite de la ventana: hoy + N días.
const limiteVentana = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Estado de una venta: vencido, por vencer y lo que vence dentro de la
// ventana. Sin plan (ventas anteriores al plan) todo el pendiente es exigible.
function derivar(r, limite) {
  const pendiente = toNum(r.pendiente);
  if (Array.isArray(r.plan) && r.plan.length) {
    const estado = estadoPlanCompletivo(r.plan, pendiente);
    const ventana = estado.partes
      .filter((p) => p.estado === "porvencer" && p.fecha <= limite)
      .reduce((s, p) => s + p.pendiente, 0);
    return {
      ...r,
      estadoplan: estado,
      vencido: estado.vencido,
      porvencer: estado.porVencer,
      proximo: estado.proximo || "",
      ventana: Math.round(ventana * 100) / 100,
      pagos: r.plan.length,
    };
  }
  return {
    ...r,
    estadoplan: null,
    vencido: pendiente,
    porvencer: 0,
    proximo: "",
    ventana: 0,
    pagos: 0,
  };
}

export default function CompletivosPendientes({ compania }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const sucursales = useSucursales({ conTodos: true });

  const [sucursal, setSucursal] = useState("0-TODOS");
  const [sucursalFija, setSucursalFija] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("todos");
  const [dias, setDias] = useState(15);
  const [busqueda, setBusqueda] = useState("");
  const [crudos, setCrudos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [planSel, setPlanSel] = useState(null);

  const cargar = useCallback(
    async (suc = sucursal) => {
      setLoading(true);
      try {
        const { ok, data } = await post("/api/completivos_pendientes", {
          sucursal: codigoSucursal(suc),
        });
        if (ok) {
          setCrudos((data.pendientes || []).map((r, i) => ({ id: i, ...r })));
          setSucursalFija(String(data.sucursalfija || ""));
        } else {
          showAlert({
            message: data.Error || "No se pudieron cargar los completivos",
            color: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [post, sucursal, showAlert]
  );

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Usuario sin alcance multi-sucursal: el selector queda en la suya.
  useEffect(() => {
    if (!sucursalFija) return;
    const propia = sucursales.find((s) => codigoSucursal(s.label) === sucursalFija);
    if (propia) setSucursal(propia.label);
  }, [sucursalFija, sucursales]);

  const nombreSucursal = useCallback(
    (codigo) => {
      const s = sucursales.find((x) => codigoSucursal(x.label) === String(codigo || "").trim());
      return s ? s.label : String(codigo || "");
    },
    [sucursales]
  );

  const limite = limiteVentana(dias);
  const filas = useMemo(() => crudos.map((r) => derivar(r, limite)), [crudos, limite]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toUpperCase();
    return filas.filter((r) => {
      if (estadoFiltro === "vencidos" && !(r.vencido > 0.009)) return false;
      if (estadoFiltro === "proximos" && !(r.ventana > 0.009)) return false;
      if (estadoFiltro === "aldia" && r.vencido > 0.009) return false;
      if (!q) return true;
      return [r.nombres, r.cedula, r.chasis, r.noprest]
        .map((x) => String(x || "").toUpperCase())
        .some((x) => x.includes(q));
    });
  }, [filas, estadoFiltro, busqueda]);

  const kpis = useMemo(
    () => ({
      ventas: visibles.length,
      pendiente: visibles.reduce((s, r) => s + toNum(r.pendiente), 0),
      vencido: visibles.reduce((s, r) => s + r.vencido, 0),
      ventana: visibles.reduce((s, r) => s + r.ventana, 0),
    }),
    [visibles]
  );

  const columns = useMemo(
    () => [
      { field: "noprest", headerName: "Préstamo", width: 95 },
      { field: "fecha", headerName: "Venta", width: 100 },
      { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 180 },
      {
        field: "cedula",
        headerName: "Documento",
        width: 135,
        valueFormatter: (value) => formatDocumentoAuto(value),
      },
      { field: "chasis", headerName: "Chasis", width: 150 },
      {
        field: "sucursal",
        headerName: "Sucursal",
        width: 140,
        valueFormatter: (value) => nombreSucursal(value),
      },
      {
        field: "pactado",
        headerName: "Pactado",
        width: 110,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => fmtMoney(value),
      },
      {
        field: "pendiente",
        headerName: "Pendiente",
        width: 115,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => fmtMoney(value),
      },
      {
        field: "vencido",
        headerName: "Vencido",
        width: 110,
        align: "right",
        headerAlign: "right",
        renderCell: ({ value }) => (
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: value > 0 ? 800 : 400,
              color: value > 0 ? FP.color.danger : FP.color.textMuted,
            }}
          >
            {fmtMoney(value)}
          </Typography>
        ),
      },
      {
        field: "ventana",
        headerName: `Vence en ${dias} días`,
        width: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => fmtMoney(value),
      },
      {
        field: "proximo",
        headerName: "Próximo pago",
        width: 115,
        valueFormatter: (value) => fechaCorta(value) || "—",
      },
      {
        field: "plan",
        headerName: "Plan",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            startIcon={<EventNoteOutlinedIcon />}
            onClick={() => setPlanSel(row)}
            sx={{ textTransform: "none", fontWeight: 700, color: FP.color.accentBlue }}
          >
            {row.pagos ? `${row.pagos} pago${row.pagos === 1 ? "" : "s"}` : "Sin plan"}
          </Button>
        ),
      },
    ],
    [dias, nombreSucursal]
  );

  const etiquetaEstado = {
    todos: "Todos",
    vencidos: "Con completivo vencido",
    proximos: `Vencen en ${dias} días`,
    aldia: "Al día",
  }[estadoFiltro];

  const imprimir = () =>
    imprimirListadoGrande({
      compania,
      titulo: "Completivos del inicial pendientes",
      subtitulo: [
        codigoSucursal(sucursal) ? `Sucursal ${sucursal}` : "Todas las sucursales",
        etiquetaEstado,
        busqueda.trim() ? `Búsqueda: ${busqueda.trim()}` : "",
      ]
        .filter(Boolean)
        .join(" · "),
      cabeceras: [
        "Préstamo",
        "Cliente",
        "Chasis",
        "Sucursal",
        "Pendiente",
        "Vencido",
        `Vence ${dias} días`,
        "Próximo",
      ],
      filas: visibles.map((r) => [
        r.noprest,
        r.nombres || "",
        r.chasis || "",
        nombreSucursal(r.sucursal),
        `RD$ ${fmtMoney(r.pendiente)}`,
        `RD$ ${fmtMoney(r.vencido)}`,
        `RD$ ${fmtMoney(r.ventana)}`,
        fechaCorta(r.proximo) || "—",
      ]),
      totales: [
        "",
        "",
        "",
        "TOTAL",
        `RD$ ${fmtMoney(kpis.pendiente)}`,
        `RD$ ${fmtMoney(kpis.vencido)}`,
        `RD$ ${fmtMoney(kpis.ventana)}`,
        "",
      ],
      numericas: [4, 5, 6],
    });

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Completivos del inicial"
        subtitle="Saldo del inicial pactado por cobrar, con sus pagos vencidos y por vencer"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard
              label="Ventas con completivo"
              value={kpis.ventas}
              icon={ReceiptLongOutlinedIcon}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label="Pendiente total"
              value={`RD$ ${fmtMoney(kpis.pendiente)}`}
              icon={PaidOutlinedIcon}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label="Vencido"
              value={`RD$ ${fmtMoney(kpis.vencido)}`}
              icon={WarningAmberOutlinedIcon}
              accent={FP.color.danger}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label={`Vence en ${dias} días`}
              value={`RD$ ${fmtMoney(kpis.ventana)}`}
              icon={ScheduleOutlinedIcon}
              accent={FP.color.warning}
            />
          </Grid>
        </Grid>

        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.soft,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <FilterAltOutlinedIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.74rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: FP.color.textSecondary,
                }}
              >
                Filtros
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3.2}>
                <TextField
                  fullWidth
                  label="Cliente, documento, chasis o préstamo"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  sx={fpInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.6}>
                <TextField
                  select
                  fullWidth
                  label="Sucursal"
                  value={sucursal}
                  disabled={!!sucursalFija}
                  onChange={(e) => {
                    setSucursal(e.target.value);
                    cargar(e.target.value);
                  }}
                  sx={fpInputSx}
                >
                  {sucursales.map((s) => (
                    <MenuItem key={s.label} value={s.label}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.6}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={estadoFiltro}
                  onChange={(e) => setEstadoFiltro(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="vencidos">Con completivo vencido</MenuItem>
                  <MenuItem value="proximos">Próximos a vencer</MenuItem>
                  <MenuItem value="aldia">Al día (nada vencido)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Próximos a vencer en"
                  value={dias}
                  onChange={(e) => setDias(Number(e.target.value))}
                  sx={fpInputSx}
                >
                  {VENTANAS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d} días
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={1.6}>
                <Button
                  fullWidth
                  startIcon={<RefreshIcon />}
                  onClick={() => cargar()}
                  disabled={loading}
                  sx={{ ...btnOutline(FP.color.navy), height: 46 }}
                >
                  Actualizar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${FP.color.border}` }}
          >
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
              Ventas con completivo pendiente
            </Typography>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Button
                size="small"
                startIcon={<PrintOutlinedIcon />}
                disabled={!visibles.length}
                onClick={imprimir}
                sx={btnOutline(FP.color.navy)}
              >
                Imprimir
              </Button>
              <Chip
                size="small"
                label={`${visibles.length} ${visibles.length === 1 ? "venta" : "ventas"}`}
                sx={{
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textSecondary,
                  fontWeight: 700,
                }}
              />
            </Stack>
          </Stack>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={visibles}
              columns={columns}
              loading={loading}
              rowHeight={48}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: "vencido", sort: "desc" }] },
              }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Card>
      </Box>

      {/* Detalle del plan de una venta */}
      <Dialog
        open={!!planSel}
        onClose={() => setPlanSel(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <EventNoteOutlinedIcon sx={{ color: FP.color.accentBlue }} />
          Plan del completivo · Préstamo {planSel?.noprest}
        </DialogTitle>
        <DialogContent>
          {planSel && (
            <>
              <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary, mb: 1.5 }}>
                {planSel.nombres} · Chasis {planSel.chasis} · Pendiente RD${" "}
                {fmtMoney(planSel.pendiente)}
              </Typography>
              {planSel.estadoplan ? (
                <PlanCompletivoResumen estado={planSel.estadoplan} />
              ) : (
                <Typography sx={{ fontSize: "0.86rem" }}>
                  Esta venta no tiene plan de fechas: todo el completivo vence desde el{" "}
                  {planSel.fecha}.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPlanSel(null)} sx={{ textTransform: "none", fontWeight: 700 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
